import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { Paginator as UntypedPaginator } from '../../components/Paginator/Paginator';
import { Loader } from '../../components/Loader/Loader';
import {
  artifactsApi,
  ArtifactArchived,
  ArtifactBundleListDTO,
  ArtifactSorting,
  ArtifactType,
} from '../../utils/artifactsApi';
import { showRequestError } from '../../utils/requestError';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { getIsSuperAdmin, getWorkspaceId, getWorkspaceRoles } from '../../utils/artifactsUi';
import { BundleCard } from '../ArtifactsPage/BundleCard';
import { ArtifactFiltersBar } from '../ArtifactsPage/ArtifactFiltersBar';
import { ArtifactRegistryTree } from './ArtifactRegistryTree';
import listStyles from '../ArtifactsPage/ArtifactsPage.module.scss';
import styles from './ArtifactRegistryPage.module.scss';

const Paginator = UntypedPaginator as unknown as React.FC<{
  size: number;
  page: number;
  total: number;
  onPageChange: (page: number) => void;
}>;

const PAGE_SIZE = 24;

export function ArtifactRegistryPage() {
  const { t } = useTranslation();
  const [bundles, setBundles] = useState<ArtifactBundleListDTO | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // registry is super-admin only; gate the fetch as well so a role-less 403
  // cannot wipe the session (API.fetch behavior)
  const [currentUser] = useCurrentUser();
  const workspaceRoles = getWorkspaceRoles(currentUser);
  const canSeeRegistry = Boolean(
    getIsSuperAdmin(currentUser) && workspaceRoles?.includes('artifacts:read'),
  );
  const myWorkspaceId = getWorkspaceId(currentUser);

  const requestedPage = Number(searchParams.get('page') ?? 1);
  const page = Number.isInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const q = searchParams.get('q') ?? '';
  const typeParams = searchParams.getAll('type') as ArtifactType[];
  const sortingParam = (searchParams.get('sorting') as ArtifactSorting) || '-published_at';
  const archivedParam = (searchParams.get('archived') as ArtifactArchived) || 'hide';
  const dateFrom = searchParams.get('date_from') ?? '';
  const dateTo = searchParams.get('date_to') ?? '';
  const scope = searchParams.get('scope') === 'mine' ? 'mine' : 'all';
  const view = searchParams.get('view') === 'tree' ? 'tree' : 'cards';

  const fetchBundles = useCallback(
    (signal?: AbortSignal) => {
      if (!canSeeRegistry) return;
      setBundles(null);
      artifactsApi
        .listRegistry(
          {
            q: q || undefined,
            types: typeParams.length > 0 ? typeParams : undefined,
            dateFrom: dateFrom || undefined,
            dateTo: dateTo || undefined,
            archived: archivedParam,
            sorting: sortingParam,
            page,
            pageSize: PAGE_SIZE,
            workspaceId: scope === 'mine' ? myWorkspaceId : undefined,
          },
          signal,
        )
        .then((data) => setBundles(data))
        .catch((error) => showRequestError(error, t('Registry')));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canSeeRegistry, q, typeParams.join(' '), dateFrom, dateTo, archivedParam, sortingParam, page, scope, myWorkspaceId, t],
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchBundles(controller.signal);
    return () => controller.abort();
  }, [fetchBundles]);

  if (currentUser && !canSeeRegistry) return <Navigate to="/artifacts" replace />;

  const setParam = (name: string, value: string) => {
    setSearchParams((params) => {
      if (value) params.set(name, value);
      else params.delete(name);
      // changing a filter resets to the first page; paging itself must not
      if (name !== 'page') params.delete('page');
      return params;
    });
  };

  const setTypes = (values: ArtifactType[]) => {
    setSearchParams((params) => {
      params.delete('type');
      values.forEach((value) => params.append('type', value));
      params.delete('page');
      return params;
    });
  };

  const setDates = (from: string, to: string) => {
    setSearchParams((params) => {
      if (from) params.set('date_from', from);
      else params.delete('date_from');
      if (to) params.set('date_to', to);
      else params.delete('date_to');
      params.delete('page');
      return params;
    });
  };

  return (
    <div className="list-content">
      <div className="page-header">
        <div>
          <div className={`breadcrumb ${listStyles.crumbs}`}>
            <span>
              <Link to="/artifacts">{t('Artifacts')}</Link>
            </span>
            <span>{t('Registry')}</span>
          </div>
          <h1 className={listStyles.pageTitle}>{t('Registry')}</h1>
        </div>
      </div>

      <ArtifactFiltersBar
        q={q}
        types={typeParams}
        dateFrom={dateFrom}
        dateTo={dateTo}
        sorting={sortingParam}
        archived={archivedParam}
        showArchivedFilter
        onSearch={(value) => setParam('q', value)}
        onTypes={setTypes}
        onDates={setDates}
        onSorting={(value) => setParam('sorting', value)}
        onArchived={(value) => setParam('archived', value === 'hide' ? '' : value)}
      >
        <div className={styles.scopeToggle}>
          <button
            type="button"
            className={cn(styles.scopeButton, { [styles.scopeActive]: scope === 'all' })}
            onClick={() => setParam('scope', '')}
          >
            {t('All workspaces')}
          </button>
          <button
            type="button"
            className={cn(styles.scopeButton, { [styles.scopeActive]: scope === 'mine' })}
            onClick={() => setParam('scope', 'mine')}
          >
            {t('My workspace')}
          </button>
        </div>
        <div className={styles.viewToggle}>
          <button
            type="button"
            className={cn(styles.scopeButton, { [styles.scopeActive]: view === 'cards' })}
            onClick={() => setParam('view', '')}
          >
            {t('Cards')}
          </button>
          <button
            type="button"
            className={cn(styles.scopeButton, { [styles.scopeActive]: view === 'tree' })}
            onClick={() => setParam('view', 'tree')}
          >
            {t('Tree')}
          </button>
        </div>
      </ArtifactFiltersBar>

      {bundles === null && <Loader />}

      {bundles !== null && bundles.objects.length === 0 && (
        <div className="noData">{t('No artifacts yet')}</div>
      )}

      {bundles !== null && bundles.objects.length > 0 && view === 'tree' && (
        <>
          <ArtifactRegistryTree bundles={bundles.objects} myWorkspaceId={myWorkspaceId} />
          <Paginator
            size={PAGE_SIZE}
            page={page}
            total={bundles.total}
            onPageChange={(newPage) => setParam('page', newPage > 1 ? String(newPage) : '')}
          />
        </>
      )}

      {bundles !== null && bundles.objects.length > 0 && view === 'cards' && (
        <>
          <div className={listStyles.grid}>
            {bundles.objects.map((bundle) => (
              <BundleCard
                key={bundle.id}
                bundle={bundle}
                to={
                  bundle.workspace_id === myWorkspaceId
                    ? `/artifacts/${bundle.id}`
                    : `/artifacts/${bundle.id}?ws=${bundle.workspace_id}`
                }
                workspaceBadge={`WS #${bundle.workspace_id}`}
              />
            ))}
          </div>
          <Paginator
            size={PAGE_SIZE}
            page={page}
            total={bundles.total}
            onPageChange={(newPage) => setParam('page', newPage > 1 ? String(newPage) : '')}
          />
        </>
      )}
    </div>
  );
}
